import { isArray, sumBy, upperCase } from 'lodash';
import { Fund } from 'src/components/Exchange/types';
import { ExchangeTransactionFrequency } from 'src/redux/modules/exchangeTransaction';
import { Bank } from 'src/types';
import {
  dailyMaximums,
  TransactionTypes,
  AUTO_DELIVERY_ENUM,
  DELIVERY_DIRECT_DEPOSIT,
  SELECTED_UNIT_ENUM_OBJECT_MAP,
} from 'src/settings/settings';
import { isPlaidBank } from '../banking/bank-utils';
import {
  isCollegeAnything,
  isRecurringBuy,
  isRecurringContribute,
  isAutoExchangeEdit,
  isAutomaticSellEdit,
  isCollegeContribute,
  isAppBuy,
} from '../transactions-util';
import {
  extractValueFromLabelValuePairArray,
  hasValue,
  isJustAdded,
} from '../utils';
import {
  ContributionYearData,
  ExchangeTransactionSubmissionPayload,
  SystematicTransaction,
  SystematicTransactionStatus,
} from './types';
import { isIRA } from '../funds/typed-funds-utility';

export const getDailyMaxLimit = (clientType, bank) => {
  if (isRecurringContribute()) {
    return dailyMaximums.recurring.college_buy;
  }

  if (isRecurringBuy()) {
    return dailyMaximums.recurring.buy;
  }

  if (isPlaidBank(bank)) {
    return isCollegeContribute()
      ? dailyMaximums.college.link.core
      : isAppBuy()
      ? dailyMaximums.manual.ras
      : dailyMaximums.link.core;
  } else {
    if (isJustAdded(bank)) {
      return isCollegeContribute()
        ? dailyMaximums.college.manualJustAdded.all
        : dailyMaximums.manualJustAdded.all;
    }
    if (isAppBuy()) {
      return dailyMaximums.manual.ras;
    }
    if (
      clientType === 'high' ||
      clientType === 'personal' ||
      clientType === 'enhanced_personal'
    ) {
      return isCollegeContribute()
        ? dailyMaximums.college.manual.enhanced_personal
        : dailyMaximums.manual.enhanced_personal;
    }
    return isCollegeContribute()
      ? dailyMaximums.college.manual.core
      : dailyMaximums.manual.core;
  }
};

export const getDailyTransactionTotal = (pendingTxns) => {
  /**
   * @Array pendingTxns this is pendingTransactions array
   */
  if (pendingTxns === undefined || pendingTxns.length === 0) {
    return 0;
  }
  let amount = 0;
  if (isArray(pendingTxns)) {
    amount = sumBy(pendingTxns, (txn) => txn.transactionRecord.amount);
  }
  return !isNaN(amount) ? amount : 0;
};

export const checkDailyMax = (
  dailyLimit: number,
  pendingTotal: number,
  shoppingCartTotal: number
) => {
  const userMax = dailyLimit - pendingTotal;
  return shoppingCartTotal > userMax;
};

export const getPaperless = (paperless, productType) => {
  // if our service fails we can assume that they have paperless settings and the backend will handle the subscription
  if (isArray(paperless) && paperless.length > 0) {
    const subscriptions = paperless[0].subscriptions;
    for (let i = 0; i < subscriptions.length; i++) {
      if (subscriptions[i].productType === productType) {
        return subscriptions[i].subscribedAll;
      }
    }
  } else {
    return true;
  }
};

export const generateExistingPurchase = (
  availableFunds: Fund[],
  paperless,
  priorPaperless,
  purchasingFunds: Fund[],
  selectedBank
) => {
  const { bankAgreementID, sameDayBank } = selectedBank;
  const transactionTargets = purchasingFunds.map((fund) => {
    const contributionYearData: ContributionYearData[] = [];
    if (fund.currentYearAmount) {
      contributionYearData.push({
        contributionYearCode: 100,
        amount: fund.currentYearAmount,
      });
    }
    if (fund.priorYearAmount) {
      contributionYearData.push({
        contributionYearCode: 101,
        amount: fund.priorYearAmount,
      });
    }
    if (fund.newFund) {
      let agreementID = '';

      if (hasValue(fund.modelAgreementUniqueIDs)) {
        const modelUniqueKey = fund.modelAgreementUniqueIDs[0];
        const [modelFund] = availableFunds.filter(
          (available) => available.uniqueKey === modelUniqueKey
        );
        if (modelFund) {
          agreementID = modelFund.agreementID;
        }
      }
      if (isCollegeAnything()) {
        return {
          modelAgreement: { agreementID },
          newFundTRPCode: fund.trpCode,
          contributionYearData,
        };
      }

      return {
        modelAgreement: { agreementID },
        newFundTicker: fund.ticker,
        contributionYearData,
      };
    }
    return {
      agreement: {
        agreementID: fund.agreementID,
      },
      contributionYearData,
    };
  });

  return {
    transactionElement: {
      transactionSource: {
        bankDto: {
          bankAgreementID,
          sameDayBank,
        },
      },
      transactionTargets,
    },
    paperlessConsent: paperless,
    currentPaperlessConsent: priorPaperless,
  };
};

export const generateTradeDatePurchase = (
  paperless,
  priorPaperless,
  purchasingFunds: Fund[],
  selectedBank
) => {
  const { bankAgreementID, sameDayBank } = selectedBank;
  const transactionTargets = purchasingFunds.map((fund) => {
    const contributionYearData: ContributionYearData[] = [];
    if (fund.currentYearAmount) {
      contributionYearData.push({
        contributionYearCode: 100,
        amount: fund.currentYearAmount,
      });
    }
    if (fund.priorYearAmount) {
      contributionYearData.push({
        contributionYearCode: 101,
        amount: fund.priorYearAmount,
      });
    }

    return {
      agreement: {
        agreementID: fund.agreementID,
        trpCode: fund.trpCode,
      },
      contributionYearData,
    };
  });

  return {
    transactionElement: {
      transactionSource: {
        bankDto: {
          bankAgreementID,
          sameDayBank,
        },
      },
      transactionTargets,
    },
    paperlessConsent: paperless,
    currentPaperlessConsent: priorPaperless,
  };
};

export const generateRecurringPurchase = (
  paperless: boolean,
  priorPaperless: boolean,
  purchasingFunds,
  selectedBank: Bank
) => {
  const { bankAgreementID, sameDayBank } = selectedBank;
  const systematicTransactions = purchasingFunds.map((fund) => {
    const {
      agreementID,
      currentYearAmount,
      investedEveryMonth,
      transferDays,
      transferMonths,
    } = fund;

    // apply extractValueFromLabelValuePairArray to each the days and months array
    const [selectedTransferDaysArray, selectedTransferMonthsArray] = [
      transferDays,
      transferMonths,
    ].map(extractValueFromLabelValuePairArray);

    return {
      amount: currentYearAmount,
      agreementId: agreementID,
      systematicActivitySchedule: {
        investedEveryMonth,
        transferDays: selectedTransferDaysArray,
        transferMonths: selectedTransferMonthsArray,
        contributionYear: {
          enumerationID: 100,
          enumerationName: 'current',
        },
      },
      systematicTransactionStatus: 'CREATE',
      systematicTransactionType: upperCase(TransactionTypes.RECURRING_BUY),
    };
  });

  return {
    bankDto: {
      bankAgreementID,
      sameDayBank,
    },
    systematicTransactions,
    paperlessConsent: paperless,
    currentPaperlessConsent: priorPaperless,
  };
};

export const generateAutoExchange = (
  _paperless: boolean,
  _priorPaperless: boolean,
  fromFund: Fund,
  toFund: Fund,
  exchangeFrequency: ExchangeTransactionFrequency,
  autoExchangeAmount: SystematicTransaction['amount'],
  iraWithholding: SystematicTransaction['iraWithholding'],
  disbursementType: SystematicTransaction['disbursementType'],
  electionAgreementUniqueId1: number,
  electionAgreementUniqueId2: number
): ExchangeTransactionSubmissionPayload => {
  const { agreementID: sourceAgreementID } = fromFund;
  const { selectedDays, selectedMonths } = exchangeFrequency;
  const { agreementID } = toFund;

  // apply extractValueFromLabelValuePairArray to each the days and months array
  const [transferDays, transferMonths] = [selectedDays, selectedMonths].map(
    extractValueFromLabelValuePairArray
  );

  const systematicTransactions: SystematicTransaction[] = [
    {
      amount: autoExchangeAmount,
      agreementId: agreementID,
      iraWithholding,
      sourceAgreementId: sourceAgreementID,
      systematicActivitySchedule: {
        investedEveryMonth: transferMonths.length === 12,
        transferDays,
        transferMonths,
      },
      systematicTransactionStatus: isAutoExchangeEdit()
        ? SystematicTransactionStatus.MODIFY
        : SystematicTransactionStatus.CREATE,
      systematicTransactionType: 'EXCHANGE',
      electionAgreementUniqueId1,
      electionAgreementUniqueId2,
      disbursementType,
    },
  ];

  return {
    systematicTransactions,
    paperlessConsent: false,
    currentPaperlessConsent: true,
  };
};

export const generateAutoSell = (
  selectedFunds,
  selectedDeliveryMethod,
  selectedBank,
  userAge
) => {
  let deferInfo = {};
  if (selectedDeliveryMethod !== DELIVERY_DIRECT_DEPOSIT) {
    deferInfo = {
      systematicTransactions: selectedFunds.map((fund) => {
        const [transferDays, transferMonths] = [
          fund.selectedDays,
          fund.selectedMonths,
        ].map(extractValueFromLabelValuePairArray);
        const federalWithholdingPercent = fund.federalWithholdingPercent
          ? fund.federalWithholdingPercent
          : 10;
        return {
          amount: fund.grossAmount,
          agreementId: fund.agreementID,
          systematicActivitySchedule: {
            investedEveryMonth: transferMonths.length === 12,
            transferDays: transferDays,
            transferMonths: transferMonths,
          },
          deliveryType: AUTO_DELIVERY_ENUM[selectedDeliveryMethod],
          fiduciaryRedemptionType:
            (userAge > 59.5 && isIRA(fund)) || !isIRA(fund)
              ? {
                  enumerationID: 1,
                  enumerationName: 'NormalRedemption',
                }
              : {
                  enumerationID: 3,
                  enumerationName: 'PrematureRedemptionNoException',
                },
          redemptionType: {
            enumerationID: 2,
            enumerationName: 'Shareholder',
          },
          iraWithholding: fund.federalWithholdingApplied
            ? federalWithholdingPercent
            : 0,
          systematicTransactionStatus: isAutomaticSellEdit()
            ? 'MODIFY'
            : 'CREATE',
          systematicTransactionType: 'REDEEM',
          electionAgreementUniqueId1: isAutomaticSellEdit()
            ? fund.electionAgreementUniqueId1
            : 0,
          electionAgreementUniqueId2: isAutomaticSellEdit()
            ? fund.electionAgreementUniqueId2
            : 0,
          disbursementType: SELECTED_UNIT_ENUM_OBJECT_MAP[fund.grossUnit],
        };
      }),
      paperlessConsent: false,
      currentPaperlessConsent: true,
    };
  } else {
    deferInfo = {
      bankDto: {
        bankAgreementID: selectedBank.bankAgreementID,
        sameDayBank: selectedBank.sameDayBank,
      },
      systematicTransactions: selectedFunds.map((fund) => {
        const [transferDays, transferMonths] = [
          fund.selectedDays,
          fund.selectedMonths,
        ].map(extractValueFromLabelValuePairArray);
        const federalWithholdingPercent = fund.federalWithholdingPercent
          ? fund.federalWithholdingPercent
          : 10;
        return {
          amount: fund.grossAmount,
          agreementId: fund.agreementID,
          systematicActivitySchedule: {
            investedEveryMonth: transferMonths.length === 12,
            transferDays: transferDays,
            transferMonths: transferMonths,
          },
          deliveryType: AUTO_DELIVERY_ENUM[selectedDeliveryMethod],
          fiduciaryRedemptionType:
            (userAge > 59.5 && isIRA(fund)) || !isIRA(fund)
              ? {
                  enumerationID: 1,
                  enumerationName: 'NormalRedemption',
                }
              : {
                  enumerationID: 3,
                  enumerationName: 'PrematureRedemptionNoException',
                },
          redemptionType: {
            enumerationID: 2,
            enumerationName: 'Shareholder',
          },
          iraWithholding: fund.federalWithholdingApplied
            ? federalWithholdingPercent
            : 0,
          disbursementType: SELECTED_UNIT_ENUM_OBJECT_MAP[fund.grossUnit],
          systematicTransactionStatus: isAutomaticSellEdit()
            ? 'MODIFY'
            : 'CREATE',
          systematicTransactionType: 'REDEEM',
          electionAgreementUniqueId1: isAutomaticSellEdit()
            ? fund.electionAgreementUniqueId1
            : 0,
          electionAgreementUniqueId2: isAutomaticSellEdit()
            ? fund.electionAgreementUniqueId2
            : 0,
        };
      }),
      paperlessConsent: false,
      currentPaperlessConsent: true,
    };
  }
  return deferInfo;
};
